import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  SendIcon,
  Spinner,
  Text,
} from '@billda/design-system';
import { usePlatformOrganisation } from '../../hooks/usePlatformOrganisation';
import { useResendOwnerInvitation } from '../../hooks/useResendOwnerInvitation';
import { useSnackbarStore } from '../../../../shared/stores/snackbarStore';
import { ResendOwnerInvitationDialog } from '../molecules/ResendOwnerInvitationDialog';
import type { OwnerInvitationResponse } from '@billda/api';

interface OrgOverviewTabProps {
  orgId: string;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getInvitationChipProps(status: string) {
  switch (status) {
    case 'Active':
      return { label: 'Active', color: 'success' as const };
    case 'InvitationSent':
      return { label: 'Invited', color: 'warning' as const };
    case 'Expired':
      return { label: 'Expired', color: 'error' as const };
    default:
      return { label: status, color: 'default' as const };
  }
}

function getResendCountLabel(resendCount: number): string {
  if (resendCount === 0) {
    return 'Not yet resent';
  }
  if (resendCount === 1) {
    return 'Resent once';
  }
  return `Resent ${resendCount} times`;
}

function FieldRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex justify-between gap-4">
      <Text as="span" variant="body2" color="secondary">
        {label}
      </Text>
      <Text as="span" variant="body2" className="text-right font-medium">
        {children}
      </Text>
    </div>
  );
}

function InvitationMeta({
  invitation,
}: {
  invitation: OwnerInvitationResponse;
}) {
  const now = new Date();
  const expiresAt = new Date(invitation.expiresAt);
  const isExpired = expiresAt.getTime() <= now.getTime();
  const expiryLabel = isExpired
    ? `Expired ${formatDateTime(invitation.expiresAt)}`
    : `Expires ${formatDateTime(invitation.expiresAt)}`;

  return (
    <Text as="div" variant="body2" color="secondary">
      {`Sent ${formatDateTime(invitation.sentAt)} · by ${invitation.sentByName} · ${expiryLabel} · ${getResendCountLabel(invitation.resendCount)}`}
    </Text>
  );
}

export const OrgOverviewTab = ({ orgId }: OrgOverviewTabProps) => {
  const { data, isLoading, error } = usePlatformOrganisation(orgId);
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);
  const { mutate: resendInvite, isPending: isResending } =
    useResendOwnerInvitation();
  const [resendDialogOpen, setResendDialogOpen] = useState(false);

  const handleResendConfirm = () => {
    resendInvite(orgId, {
      onSuccess: () => {
        setResendDialogOpen(false);
        showSnackbar('Invitation resent', 'success');
      },
    });
  };

  if (isLoading && !data) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <Spinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert severity="error" className="mb-4">
        {error?.message ?? 'Organisation not found'}
      </Alert>
    );
  }

  const { owner, invitation } = data;
  const invitationChip = invitation
    ? getInvitationChipProps(invitation.status)
    : { label: '—', color: 'default' as const };

  return (
    <div className="px-4 py-4 sm:px-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          className="rounded-xl border border-border-default bg-surface-default p-4"
          data-testid="org-overview-organisation-card"
        >
          <Text as="div" variant="body2" className="mb-3 font-medium">
            Organisation
          </Text>
          <div className="flex flex-col gap-3">
            <FieldRow label="Name">{data.name}</FieldRow>
            <FieldRow label="Slug">{data.slug}</FieldRow>
            <FieldRow label="Created">{formatDate(data.createdAt)}</FieldRow>
          </div>
        </div>
        <div
          className="rounded-xl border border-border-default bg-surface-default p-4"
          data-testid="org-overview-owner-card"
        >
          <Text as="div" variant="body2" className="mb-3 font-medium">
            Owner
          </Text>
          <div className="flex flex-col gap-3">
            <FieldRow label="Name">{owner?.name ?? '—'}</FieldRow>
            <FieldRow label="Email">{owner?.email ?? '—'}</FieldRow>
            <FieldRow label="Membership">
              {owner?.membershipStatus ?? '—'}
            </FieldRow>
          </div>
        </div>
      </div>

      <div
        className="mt-4 rounded-lg border border-border-default p-4"
        data-testid="owner-invitation-block"
      >
        <div
          className={`flex items-center justify-between ${
            invitation ? 'mb-2' : 'mb-0'
          }`}
        >
          <Text as="div" variant="body2" className="font-medium">
            Owner invitation
          </Text>
          <Chip
            label={invitationChip.label}
            color={invitationChip.color}
            size="sm"
            className="text-[0.7rem]"
          />
        </div>

        {invitation && <InvitationMeta invitation={invitation} />}

        {invitation?.canResend && (
          <Button
            variant="neutral"
            className="mt-4"
            onClick={() => setResendDialogOpen(true)}
            disabled={isResending}
            data-testid="overview-resend-button"
          >
            <SendIcon aria-hidden="true" className="size-4" />
            Resend invitation
          </Button>
        )}
      </div>

      <ResendOwnerInvitationDialog
        open={resendDialogOpen}
        email={owner?.email ?? ''}
        organisationName={data.name}
        isLoading={isResending}
        onConfirm={handleResendConfirm}
        onCancel={() => setResendDialogOpen(false)}
      />
    </div>
  );
};
